import { Plus, Search } from "lucide-react";
import { useDebounce } from "@noa/ui";
import { Button, Input, Skeleton } from "~/components/ui";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CreateConceptModal } from "~/components/lexicon/create-concept-modal";
import { CreateSubjectModal } from "~/components/lexicon/create-subject-modal";
import { LexiconSubjectConcepts } from "~/components/lexicon/lexicon-subject-concepts";
import { useGetLexiconSubjects } from "@noa/api-client";
import { useCurrentLanguage } from "@noa/i18n";
import { Screen } from "~/screens/screen";
import type { LexiconConceptResponse } from "@noa/api-core";
import { UpdateConceptModal } from "~/components/lexicon/update-concept-modal";
import { DeleteConceptModal } from "~/components/lexicon/delete-concept-modal";
import { ViewConceptModal } from "~/components/lexicon/view-concept-modal";
import type { LexiconSubject } from "~/common/lexicon-subject";
import { subjectId } from "~/utils/lexicon";

export function LexiconScreen() {
  const { t } = useTranslation("lexicon");
  const appLanguage = useCurrentLanguage();
  const [showAddSubjectModal, setShowAddSubjectModal] = useState(false);
  const [showAddConceptModal, setShowAddConceptModal] = useState(false);
  const [subjectIdToAddConcept, setSubjectIdToAddConcept] = useState<LexiconSubject["id"] | null>(null);
  const [conceptToView, setConceptToView] = useState<LexiconConceptResponse | null>(null);
  const [conceptToUpdate, setConceptToUpdate] = useState<LexiconConceptResponse | null>(null);
  const [conceptToDelete, setConceptToDelete] = useState<LexiconConceptResponse | null>(null);
  const [subjectSearch, setSubjectSearch] = useState("");
  const debouncedSubjectSearch = useDebounce(subjectSearch, 300);
  const { subjects: apiSubjects, isLoadingSubjects } = useGetLexiconSubjects();
  const subjects: LexiconSubject[] = useMemo(() => {
    const searchTerm = debouncedSubjectSearch.trim().toLowerCase();
    return [...apiSubjects]
      .filter((s) => {
        if (!searchTerm) return true;
        const fr = (s.fr ?? "").toLowerCase();
        const en = (s.en ?? "").toLowerCase();
        return fr.includes(searchTerm) || en.includes(searchTerm);
      })
      .sort((a, b) => {
        const va = a[appLanguage] ?? a[appLanguage === "fr" ? "en" : "fr"] ?? "";
        const vb = b[appLanguage] ?? b[appLanguage === "fr" ? "en" : "fr"] ?? "";
        return va.localeCompare(vb);
      })
      .map((s) => ({ ...s, id: subjectId(s) }));
  }, [apiSubjects, appLanguage, debouncedSubjectSearch]);

  // What the modals may offer, which is not what the screen lists.
  //
  // `subjects` above is narrowed by the search box, and the modals look their
  // selected subject back up in whatever array they are handed — so sharing that
  // one meant typing in the search while a modal was open could drop the chosen
  // subject out from under it, and the submit then failed with no explanation.
  // Deriving from `apiSubjects` keeps the options stable for as long as the
  // modal is open.
  //
  // Bilingual-only for the same reason the chat card filters: the request body
  // needs both translations, so a half-translated subject can only fail at
  // submit. Better never offered than offered and dead.
  // Sorted in the app language, like the screen list: the API returns them
  // ordered by their English label, so without this the French dropdown reads as
  // unsorted — the modals used to inherit the sort from `subjects`.
  const selectableSubjects: LexiconSubject[] = useMemo(
    () =>
      apiSubjects
        .filter((s) => s.fr && s.en)
        .map((s) => ({ ...s, id: subjectId(s) }))
        .sort((a, b) => (a[appLanguage] ?? "").localeCompare(b[appLanguage] ?? "")),
    [apiSubjects, appLanguage]
  );

  // Stable reference required: passed to LexiconSubjectConcepts, which is memo()'d
  const handleAddConcept = useCallback((subjectId: string) => {
    setSubjectIdToAddConcept(subjectId);
    setShowAddConceptModal(true);
  }, []);

  const handleCloseAddConceptModal = () => {
    setShowAddConceptModal(false);
    setSubjectIdToAddConcept(null);
  };

  return (
    <Screen>
      <div id="lexicon-content" className="flex h-full w-full flex-col">
        <div className="flex flex-wrap items-center justify-between gap-2 py-4 px-3 xs:px-6 sticky top-0 z-10 shadow-[0_2px_8px_rgba(0,0,0,0.06)] bg-noa border-b border-noa">
          <Input
            placeholder={t("searchPlaceholder")}
            prefix={<Search size={14} />}
            value={subjectSearch}
            onChange={(e) => setSubjectSearch(e.target.value)}
            allowClear
            className="min-w-64 max-w-125"
          />
          <div className="flex gap-2">
            <Button
              className="min-w-32 pl-2 pr-4"
              icon={<Plus size={14} />}
              onClick={() => setShowAddSubjectModal(true)}
            >
              {t("fields.subject")}
            </Button>
            <Button
              className="min-w-32 pl-2 pr-5"
              icon={<Plus size={14} />}
              onClick={() => setShowAddConceptModal(true)}
            >
              Concept
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-2 pt-4 px-3 xs:px-6 pb-6 overflow-y-auto flex-1">
          {isLoadingSubjects ? (
            <Skeleton active />
          ) : (
            subjects.map((subject) => (
              <LexiconSubjectConcepts
                key={subject.id}
                language={appLanguage}
                subject={subject}
                onAddConcept={handleAddConcept}
                setConceptToView={setConceptToView}
                setConceptToUpdate={setConceptToUpdate}
                setConceptToDelete={setConceptToDelete}
              />
            ))
          )}
        </div>
      </div>
      <ViewConceptModal
        concept={conceptToView}
        language={appLanguage}
        close={() => setConceptToView(null)}
        onEdit={setConceptToUpdate}
      />
      <CreateConceptModal
        isOpen={showAddConceptModal}
        language={appLanguage}
        selectedSubjectId={subjectIdToAddConcept}
        subjects={selectableSubjects}
        closeModal={handleCloseAddConceptModal}
      />
      <UpdateConceptModal
        concept={conceptToUpdate}
        subjects={selectableSubjects}
        language={appLanguage}
        close={() => {
          setConceptToUpdate(null);
        }}
      />
      <DeleteConceptModal
        isOpen={!!conceptToDelete}
        setIsOpen={(open) => {
          if (!open) setConceptToDelete(null);
        }}
        concept={conceptToDelete}
      />
      <CreateSubjectModal isOpen={showAddSubjectModal} closeModal={() => setShowAddSubjectModal(false)} />
    </Screen>
  );
}
